// 加工指示明細の代表行を entrydata へ投影する処理をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createKakousijiRepresentativeProjectionService(deps) {
    const {
        DeepCopy,
        DicVal,
        GridApi,
        GridData,
        KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE,
        KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE,
        KAKOUSIJI_QUALITY_BASE_PANEL_TYPE,
        KAKOUSIJI_QUALITY_DISPLAY_PANEL_TYPE,
        SetGridData,
        columninfo,
        columns,
        createKakousijiGroupRow,
        dispatch,
        entrydata,
        entrydataRef,
        entryrerender,
        fnc,
        getKakousijiGridRowsByType,
        isEmpty,
        isKakousijiGroupManagedPanelType,
        nextKakousijiDetailRowId,
        normalizeKakousijiDemandRows,
        normalizeKakousijiGroupNo,
        normalizeKakousijiText,
        normalizeKakousijiUpperColumnId,
        rerender,
        resolveKakousijiActiveGroupSelection,
        resolveKakousijiGridPanelByType,
        resolveKakousijiGroupItemContext,
        rowMatchesKakousijiItemGroup,
        stopKakousijiGridEditing,
        syncKakousijiGridRowsFromApi
    } = deps;

    function flushKakousijiListReflectSourcePanels() {
        const sourcePanels = [
            resolveKakousijiGridPanelByType(KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE),
            resolveKakousijiGridPanelByType(KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE),
            resolveKakousijiGridPanelByType(KAKOUSIJI_QUALITY_BASE_PANEL_TYPE),
            resolveKakousijiGridPanelByType(KAKOUSIJI_QUALITY_DISPLAY_PANEL_TYPE)
        ].filter((panelInfo) => !isEmpty(panelInfo));
        sourcePanels.forEach((panelInfo) => stopKakousijiGridEditing(panelInfo));
        sourcePanels.forEach((panelInfo) => syncKakousijiGridRowsFromApi(panelInfo));
    }
    function isSameKakousijiGridRows(before, after) {
        if (before === after) return true;
        if (!Array.isArray(before) || !Array.isArray(after)) return false;
        if (before.length !== after.length) return false;
        try {
            return JSON.stringify(before) === JSON.stringify(after);
        } catch (error) {
            return false;
        }
    }
    function setKakousijiGridRows(panelInfo, rows) {
        if (isEmpty(panelInfo)) return;
        const nextRows = normalizeKakousijiDemandRows(panelInfo, Array.isArray(rows) ? rows : []);
        // 同じ内容なら何もしない。無条件に modify / entryrerender / rerender を発行すると、
        // 再描画→マスタ照会→ValueChanged→本関数、の無限ループになる。
        if (isSameKakousijiGridRows(GridData(panelInfo), nextRows)) return;
        SetGridData(panelInfo, nextRows);
        const api = GridApi(panelInfo);
        if (!isEmpty(api)) {
            if (typeof api.setGridOption === 'function') {
                api.setGridOption('rowData', nextRows);
            } else if (typeof api.setRowData === 'function') {
                api.setRowData(nextRows);
            }
            if (typeof api.refreshCells === 'function') {
                api.refreshCells({ force: true });
            }
        }
        // 画面が破棄された後にコールバックが届くことがあるため null を許容する。
        if (!isEmpty(fnc.current)) fnc.current.modify(true);
        dispatch(entryrerender());
        // rerender()(update トグル)は全パネルの再構築を引き起こし、
        // マスタ名称が1件確定するたびに画面全体が消えて見える。
        // グリッドへは上で ag-grid API 経由で反映済みのため、
        // 必要な後続処理である代表行投影だけを直接同期する。
        syncKakousijiRepresentativeEntryProjection(DicVal(panelInfo, 'type', ''), null);
    }
    function buildKakousijiKnownColumnIds(panelType) {
        const targetType = normalizeKakousijiText(panelType);
        const ids = [];
        const add = (value) => {
            const id = normalizeKakousijiUpperColumnId(value);
            if (id === '' || ids.includes(id)) return;
            ids.push(id);
        };
        (Array.isArray(columninfo) ? columninfo : []).filter((col) => normalizeKakousijiText(DicVal(col, 'type', '')) === targetType).forEach((col) => add(DicVal(col, 'columnid', '')));
        (Array.isArray(columns) ? columns : []).filter((col) => normalizeKakousijiText(DicVal(col, 'type', '')) === targetType).forEach((col) => add(DicVal(col, 'columnid', DicVal(col, 'field', ''))));
        getKakousijiGridRowsByType(targetType).forEach((row) => Object.keys(isEmpty(row) ? {} : row).forEach((key) => add(key)));
        return ids;
    }
    function getKakousijiRepresentativeFieldPrefixes(panelType) {
        const targetType = normalizeKakousijiText(panelType);
        if (targetType === KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE) return ['SSDZ_'];
        if (targetType === KAKOUSIJI_QUALITY_BASE_PANEL_TYPE) return ['SSDQ_'];
        return [];
    }
    function getKakousijiRepresentativeFieldIds(panelType) {
        const prefixes = getKakousijiRepresentativeFieldPrefixes(panelType);
        return buildKakousijiKnownColumnIds(panelType).filter((fieldId) => prefixes.some((prefix) => normalizeKakousijiUpperColumnId(fieldId).startsWith(prefix)));
    }
    function clearKakousijiRepresentativeFields(entryObj, panelType) {
        if (isEmpty(entryObj) || typeof entryObj !== 'object') return false;
        const fieldIds = getKakousijiRepresentativeFieldIds(panelType);
        let changed = false;
        fieldIds.forEach((fieldId) => {
            [fieldId, fieldId + '_nm', fieldId + '_NM', fieldId + '_cd', fieldId + '_CD'].forEach((key) => {
                if (entryObj[key] === '') return;
                entryObj[key] = '';
                changed = true;
            });
        });
        return changed;
    }
    function pickKakousijiRepresentativeRow(panelType, contextOverride = null) {
        const context = {
            itemCd: normalizeKakousijiText(DicVal(contextOverride, 'itemCd', '')),
            groupNo: normalizeKakousijiGroupNo(DicVal(contextOverride, 'groupNo', ''))
        };
        const rows = getKakousijiGridRowsByType(panelType);
        if (context.itemCd === '') return { row: null, index: -1, context };
        let index = (Array.isArray(rows) ? rows : []).findIndex((row) => rowMatchesKakousijiItemGroup(row, context.itemCd, context.groupNo));
        if (index < 0 && context.groupNo === '') {
            index = (Array.isArray(rows) ? rows : []).findIndex((row) => {
                const rowContext = resolveKakousijiGroupItemContext(row);
                return rowContext.itemCd === context.itemCd && rowContext.groupNo === '';
            });
        }
        return { row: index >= 0 ? rows[index] : null, index, context };
    }
    function mergeKakousijiRepresentativeRowToEntry(panelType, contextOverride = null) {
        const targetType = normalizeKakousijiText(panelType);
        if (isEmpty(entrydataRef.current) || !isKakousijiGroupManagedPanelType(targetType)) return false;
        if (entrydataRef.current !== null && typeof entrydataRef.current === 'object' && Object.isFrozen(entrydataRef.current)) {
            // Redux(Immer)に渡ったオブジェクトは凍結される。参照を持ち続けると以降の書き込みが例外になる。
            if (typeof console !== 'undefined' && console.warn) {
                console.warn('[perf] entrydataRef.current が凍結されていたため複製しました', new Error('freeze-origin').stack);
            }
            entrydataRef.current = { ...entrydataRef.current };
        }
        const entryObj = entrydataRef.current;
        // contextが未確定(itemCd空)なら現在の選択を解決して使う。
        // それでも未確定なら既存の表示値を消さずに終了する。選択解除時のクリアは
        // clearActiveKakousijiSelectionData が担うため、ここでのクリアは不要。
        let effectiveContext = contextOverride;
        if (normalizeKakousijiText(DicVal(effectiveContext, 'itemCd', '')) === '') {
            effectiveContext = resolveKakousijiActiveGroupSelection(targetType);
        }
        if (normalizeKakousijiText(DicVal(effectiveContext, 'itemCd', '')) === '') {
            return false;
        }
        const picked = pickKakousijiRepresentativeRow(targetType, effectiveContext);
        const row = DicVal(picked, 'row', null);
        const fieldIds = getKakousijiRepresentativeFieldIds(targetType);
        // 変更判定は「処理前後で実際に値が変わったか」で行う。
        // クリア→書き戻しで元と同じ値に戻っただけでも changed=true を返すと、
        // entrydata/entryrerender 発行→再描画→本関数、の無限ループになる。
        const watchKeys = [];
        fieldIds.forEach((fieldId) => {
            [fieldId, fieldId + '_cd', fieldId + '_CD', fieldId + '_nm', fieldId + '_NM'].forEach((key) => {
                if (!watchKeys.includes(key)) watchKeys.push(key);
            });
        });
        const normalizeProjectionValue = (value) => (value === undefined || value === null ? '' : value);
        const beforeValues = {};
        watchKeys.forEach((key) => { beforeValues[key] = normalizeProjectionValue(entryObj[key]); });
        const finishMerge = () => {
            let changedAfter = false;
            watchKeys.forEach((key) => {
                if (beforeValues[key] !== normalizeProjectionValue(entryObj[key])) changedAfter = true;
            });
            return changedAfter;
        };
        clearKakousijiRepresentativeFields(entryObj, targetType);
        if (isEmpty(row)) {
            return finishMerge();
        }
        fieldIds.forEach((fieldId) => {
            if (Object.prototype.hasOwnProperty.call(row, fieldId) && entryObj[fieldId] !== row[fieldId]) {
                entryObj[fieldId] = row[fieldId];
            }
            [fieldId + '_cd', fieldId + '_CD', fieldId + '_nm', fieldId + '_NM'].forEach((displayFieldId) => {
                if (!Object.prototype.hasOwnProperty.call(row, displayFieldId)) return;
                if (entryObj[displayFieldId] === row[displayFieldId]) return;
                entryObj[displayFieldId] = row[displayFieldId];
            });
        });
        // 行が持たない表示用の値(_nm/_cd)は、基の値が変わっていない限りクリア前の値へ戻す。
        // 表示名はマスタ照会で後から導出される値のため、ここで消すと
        // 「投影が消す → マスタ照会が書き戻す」の往復で entrydata が揺れ続け、無限ループになる。
        fieldIds.forEach((fieldId) => {
            ['_cd', '_CD', '_nm', '_NM'].forEach((suffix) => {
                const displayFieldId = fieldId + suffix;
                if (Object.prototype.hasOwnProperty.call(row, displayFieldId)) return;
                if (normalizeProjectionValue(entryObj[fieldId]) !== beforeValues[fieldId]) return;
                if (normalizeProjectionValue(entryObj[displayFieldId]) === beforeValues[displayFieldId]) return;
                entryObj[displayFieldId] = beforeValues[displayFieldId];
            });
        });
        const projectedValues = {};
        fieldIds.forEach((fieldId) => {
            [fieldId, fieldId + '_cd', fieldId + '_CD', fieldId + '_nm', fieldId + '_NM'].forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(entryObj, key)) projectedValues[key] = entryObj[key];
            });
        });
        return finishMerge();
    }
    function syncKakousijiRepresentativeEntryProjection(panelType, contextOverride = null) {
        const targetType = normalizeKakousijiText(panelType);
        const changed = mergeKakousijiRepresentativeRowToEntry(targetType, contextOverride);
        if (!changed || isEmpty(entrydataRef.current)) return false;
        dispatch(entrydata({ ...entrydataRef.current }));
        dispatch(entryrerender());
        return true;
    }
    function resolveKakousijiRepresentativePanelTypeByFieldId(fieldId) {
        const target = normalizeKakousijiUpperColumnId(fieldId);
        if (target.startsWith('SSDZ_')) return KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE;
        if (target.startsWith('SSDQ_')) return KAKOUSIJI_QUALITY_BASE_PANEL_TYPE;
        return '';
    }
    function applyKakousijiBaseUpdatesToCurrentContext(panelType, updates, contextOverride = null) {
        const targetType = normalizeKakousijiText(panelType);
        const panelInfo = resolveKakousijiGridPanelByType(targetType);
        const context = contextOverride || resolveKakousijiActiveGroupSelection(targetType);
        const itemCd = normalizeKakousijiText(DicVal(context, 'itemCd', ''));
        const groupNo = normalizeKakousijiGroupNo(DicVal(context, 'groupNo', ''));
        if (isEmpty(panelInfo) || itemCd === '' || groupNo === '' || isEmpty(updates)) return false;
        const currentRows = DeepCopy(syncKakousijiGridRowsFromApi(panelInfo));
        const targetIndexes = [];
        (Array.isArray(currentRows) ? currentRows : []).forEach((row, index) => {
            if (rowMatchesKakousijiItemGroup(row, itemCd, groupNo)) targetIndexes.push(index);
        });
        if (targetIndexes.length === 0) {
            const nextRow = createKakousijiGroupRow(targetType, itemCd, groupNo);
            nextRow.rowid = String(nextKakousijiDetailRowId(currentRows));
            Object.keys(updates || {}).forEach((key) => {
                if (isEmpty(key)) return;
                nextRow[key] = updates[key];
            });
            currentRows.push(nextRow);
            setKakousijiGridRows(panelInfo, currentRows);
            return true;
        }
        let changed = false;
        const nextRows = currentRows.map((row, index) => {
            if (!targetIndexes.includes(index) || isEmpty(row)) return row;
            const nextRow = { ...row };
            Object.keys(updates || {}).forEach((key) => {
                if (isEmpty(key)) return;
                if (nextRow[key] === updates[key]) return;
                nextRow[key] = updates[key];
                changed = true;
            });
            return nextRow;
        });
        if (!changed) return false;
        setKakousijiGridRows(panelInfo, nextRows);
        return true;
    }
    function isKakousijiEmptyGridRow(row, targetColumns) {
        if (isEmpty(row) || typeof row !== 'object') return true;
        const ignoreKeys = new Set(['ROWID', 'GRIP', 'EXPAND', 'ROW_KIND', 'DETAIL', 'ROWKEY']);
        const targets = Array.isArray(targetColumns) ? targetColumns : [];
        const keys = Object.keys(row);
        for (let i = 0; i < keys.length; i++) {
            const key = normalizeKakousijiUpperColumnId(keys[i]);
            if (ignoreKeys.has(key)) continue;
            if (targets.length !== 0 && targets.indexOf(key) === -1) continue;
            if (normalizeKakousijiText(row[keys[i]]) !== '') return false;
        }
        return true;
    }

    return {
        flushKakousijiListReflectSourcePanels,
        setKakousijiGridRows,
        buildKakousijiKnownColumnIds,
        getKakousijiRepresentativeFieldPrefixes,
        getKakousijiRepresentativeFieldIds,
        clearKakousijiRepresentativeFields,
        pickKakousijiRepresentativeRow,
        mergeKakousijiRepresentativeRowToEntry,
        syncKakousijiRepresentativeEntryProjection,
        resolveKakousijiRepresentativePanelTypeByFieldId,
        applyKakousijiBaseUpdatesToCurrentContext,
        isKakousijiEmptyGridRow
    };
}
